import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/Tooltip";
import { Avatar } from "@/components/ui/Avatar";

export interface ResponsibleTeam {
  label: string;
  users: { id: string; name: string; initials: string; avatar_url?: string | null; role: string }[];
}

const CIRCLE = "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-background bg-muted text-[10px] font-medium";

// Responsibility as avatar stacks (UX doc §3/§4b): capped at 3 + "+N", team
// name in a tooltip. Empty team is a first-class warning state, not a blank
// cell — "No one assigned" uses the same chip treatment as the blocking chip.
export default function AvatarStack({ team, claimedBy, className }: {
  team: ResponsibleTeam | null;
  claimedBy?: { name: string; initials: string; avatar_url?: string | null } | null;
  className?: string;
}) {
  if (claimedBy) {
    // One person, named — visually unmistakable from the team stack, which
    // is the entire argument for claiming (spec §4 "Responsibility" slot).
    return (
      <span className={cn("inline-flex shrink-0 items-center gap-1.5", className)}>
        <Avatar name={claimedBy.name} initials={claimedBy.initials} avatarUrl={claimedBy.avatar_url} className="border border-background" />
        <span className="text-xs font-medium text-foreground">{claimedBy.name.split(/\s+/)[0]}</span>
      </span>
    );
  }

  if (!team || team.users.length === 0) {
    return (
      <span className={cn("inline-flex h-[22px] shrink-0 items-center rounded-full bg-warning-surface px-2 text-xs text-warning", className)}>
        No one assigned
      </span>
    );
  }

  const shown = team.users.slice(0, 3);
  const extra = team.users.length - shown.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          tabIndex={0}
          className={cn("flex items-center gap-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className)}
          aria-label={team.label}
        >
          {shown.map((user) => (
            <Avatar key={user.id} name={user.name} initials={user.initials} avatarUrl={user.avatar_url} className="border border-background" />
          ))}
          {extra > 0 && <span className={cn(CIRCLE, "text-muted-foreground")}>+{extra}</span>}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">{team.label}</span>
          {team.users.map((user) => (
            <span key={user.id}>{user.name} — {user.role}</span>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
